import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { fetchAiFilterValues } from '../../../actions/aiFilterValues.actions';
import { searchTopics } from '../../../actions/search.actions';
import { colors } from '../../../assets/styles/themes/variables';
import constants, {
	filterLabels,
	inclusionAdapted,
	NONE_OF_THE_ABOVE_OPTION,
	pageContentTypes,
	searchSettings,
	teachingFormats,
	teachingMethods,
} from '../../../constants';
import { selectAiFilterValues } from '../../../reducers/aiFilterValues.selectors';
import {
	selectCoursesPublishers,
	selectDocumentsPublishers,
} from '../../../reducers/publishers.selectors';
import { selectSchoolTypes } from '../../../reducers/schoolTypes.selectors';
import { getSubTopics, selectWorkloads } from '../../../reducers/search.selectors';
import useQueryModification from '../../../routes/SearchPage/useQueryModification';
import type { SearchFilters } from '../../../types/search';
import queryToSearchFilters from '../../../utils/queryToSearchFilters';
import searchResultsStorageManager from '../../../utils/searchResultsStorageManager';
import sortAlphabetically from '../../../utils/sortAlphabetically';
import useAction from '../../../utils/useAction';
import useRouter from '../../../utils/useRouter';
import useScreenSize from '../../../utils/useScreenSize';
import useSearchExperiments from '../../../utils/useSearchExperiments';
import CustomDialog from '../../Dialog/CustomDialog';
import {
	type FilterOption,
	SearchFiltersAccordion,
	type SearchFiltersAccordionProps,
} from './SearchFilterAccordion';

interface SearchFiltersDialogProps {
	isOpen: boolean;
	handleClose?: () => void;
	scrollTo?: AccordionType;
	filtersOrder: AccordionType[];
	type: 'core' | 'course';
}

export const SCROLL_DELAY = 350;

export type AccordionType =
	| 'subject'
	| 'classYear'
	| 'schoolType'
	| 'publisher'
	| 'subTopic'
	| 'learningFormat'
	| 'teachingFormat'
	| 'teachingMethod'
	| 'contentType'
	| 'pageContentType'
	| 'inclusionAdapted'
	| 'workload'
	| 'courseTheme'
	| 'courseType'
	| 'courseDuration';

type BooleanAccordionType = Extract<AccordionType, 'inclusionAdapted'>;
type ArrayAccordionType = Exclude<AccordionType, BooleanAccordionType>;

type ExpandedState = Record<AccordionType, boolean>;

type Options = {
	[K in AccordionType]: FilterOption[];
};

const SearchFiltersDialog: React.FC<SearchFiltersDialogProps> = ({
	isOpen,
	handleClose,
	scrollTo,
	filtersOrder,
	type = 'core',
}) => {
	const { t } = useTranslation();
	const publishers = useSelector(
		type === 'core' ? selectDocumentsPublishers : selectCoursesPublishers
	);
	const schoolTypes = useSelector(selectSchoolTypes);
	const aiFilterValues = useSelector(selectAiFilterValues);
	const { useAiFilters } = useSearchExperiments();
	const workloads = useSelector(selectWorkloads);
	const subtopicsSelector = useSelector(getSubTopics);
	const searchTopicsAction = useAction(searchTopics);
	const fetchAiFilterValuesAction = useAction(fetchAiFilterValues);
	const { router, match } = useRouter();
	const { updateQueryParams } = useQueryModification<SearchFilters>(router, match);
	const isClearingRef = useRef(false);

	const requestFilters = useMemo(() => {
		return queryToSearchFilters(match.location.search);
	}, [match.location.search]);

	const accordionsInitialState: ExpandedState = {
		subject: true,
		classYear: true,
		schoolType: true,
		publisher: true,
		subTopic: false,
		learningFormat: false,
		teachingFormat: true,
		teachingMethod: true,
		contentType: true,
		pageContentType: true,
		inclusionAdapted: true,
		workload: false,
		courseTheme: true,
		courseType: true,
		courseDuration: true,
	};

	type AccordionRecord = Record<AccordionType, React.RefObject<HTMLDivElement>>;

	const useAccordionReduceFunction = (acc: AccordionRecord, key: string) => {
		acc[key as AccordionType] = useRef<HTMLDivElement>(null);
		return acc;
	};

	// create a list of refs from the AccordionTypes
	const refs = Object.keys(accordionsInitialState).reduce(
		useAccordionReduceFunction,
		{} as Record<AccordionType, React.RefObject<HTMLDivElement>>
	);

	const optionsInitialState = {
		subject: sortAlphabetically(
			constants.subjects.filter((subject) => subject.id !== NONE_OF_THE_ABOVE_OPTION),
			{ field: 'text' }
		).map(({ text, id }) => ({
			name: text,
			value: id,
			isSelected: requestFilters.subject?.includes(id),
		})),
		classYear: Array.from({ length: 13 }, (_, i) => i + 1).map((year) => ({
			name: year.toString(),
			value: year.toString(),
			isSelected: requestFilters.classYear?.includes(year.toString()),
		})),
		schoolType: [],
		publisher: [],
		subTopic: [],
		learningFormat: constants.learningFormats.map((format) => ({
			name: format.text,
			value: format.value,
			isSelected: requestFilters.learningFormat?.includes(format.value),
		})),
		teachingFormat: teachingFormats.map((format) => ({
			name: t(format.label),
			value: format.key,
			isSelected: requestFilters.teachingFormat?.includes(format.key),
		})),
		teachingMethod: teachingMethods.map((method) => ({
			name: t(method.label),
			value: method.key,
			isSelected: requestFilters.teachingMethod?.includes(method.key),
		})),
		contentType: [],
		pageContentType: pageContentTypes.map((ct) => ({
			name: t(ct.label),
			value: ct.key,
			isSelected: (requestFilters.pageContentType ?? []).some((v) => String(v) === ct.key),
		})),
		inclusionAdapted: inclusionAdapted.map((item) => ({
			name: t(item.label),
			value: item.key,
			isSelected: !!requestFilters.inclusionAdapted,
		})),
		workload: [],
		courseTheme: constants.courseThemes.map((theme) => ({
			name: theme.text,
			value: theme.value,
			isSelected: requestFilters.courseTheme?.includes(theme.value),
		})),
		courseType: constants.courseTypes.map((type) => ({
			name: type.text,
			value: type.value,
			isSelected: requestFilters.courseType?.includes(type.value),
		})),
		courseDuration: constants.courseDuration.map((duration) => ({
			name: duration.text,
			value: duration.value,
			isSelected: requestFilters.courseType?.includes(duration.value),
		})),
	};

	const [expanded, setExpanded] = useState<ExpandedState>(accordionsInitialState);
	const [filterOptions, setFilterOptions] = useState<Options>(optionsInitialState);

	const toggleAccordion = (accordion: AccordionType) => {
		setExpanded((prev) => ({ ...prev, [accordion]: !prev[accordion] }));
	};

	const { isXsScreen, isSmScreen } = useScreenSize();
	const isMobile = isXsScreen;
	const isTablet = isSmScreen;
	const isAnySubjectSelected = filterOptions.subject.some((subject) => subject.isSelected);
	const numberOfSelectedSubjects = filterOptions.subject.filter(
		(subject) => subject.isSelected
	).length;

	useEffect(() => {
		if (isOpen && !isClearingRef.current) {
			setFilterOptions((prevFilterOptions) => {
				const updatedFilterOptions = { ...prevFilterOptions };

				Object.keys(accordionsInitialState).forEach((key) => {
					const accordion = key as AccordionType;
					const selectedValues = requestFilters[accordion];

					const resolveSelected = (option: FilterOption): boolean => {
						if (typeof selectedValues === 'boolean') return selectedValues;
						if (Array.isArray(selectedValues) && selectedValues.length > 0) {
							return selectedValues.some((v) => String(v) === String(option.value));
						}
						return false;
					};

					const mapped = prevFilterOptions[accordion].map((option) => ({
						...option,
						isSelected: resolveSelected(option),
					}));

					updatedFilterOptions[accordion] =
						accordion === 'subject' || accordion === 'publisher'
							? mapped.sort((a, b) => Number(b.isSelected) - Number(a.isSelected))
							: mapped;
				});

				return updatedFilterOptions;
			});
		}
		// Reset the clearing flag after the effect runs
		if (isClearingRef.current) {
			isClearingRef.current = false;
		}
	}, [isOpen, requestFilters]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		// SEAR-460: in AI mode the school-type (and subject) options come from the
		// AI possible-values endpoint (effect below), not the mongo CP school types.
		if (useAiFilters) return;
		const schoolTypeOptions = schoolTypes
			.filter((schoolType) => schoolType.searchId)
			.map((schoolType) => {
				let isSchoolTypeSelected: boolean | undefined;
				if (requestFilters.schoolType && schoolType.searchId) {
					isSchoolTypeSelected = requestFilters.schoolType.includes(schoolType.searchId);
				}
				return {
					name: schoolType.name,
					value: schoolType.searchId as string,
					isSelected: isSchoolTypeSelected,
				};
			});
		if (schoolTypeOptions.length > 0) {
			setFilterOptions((prev) => ({ ...prev, schoolType: schoolTypeOptions }));
		}
	}, [schoolTypes.length, useAiFilters]);

	// SEAR-460: when the AI-filters flag is on, fetch the canonical AI filter
	// values and source the subject + school-type options from them (value =
	// the AI searchId, which equals the AI name). classYear is unchanged — AI and
	// CP class years share the '1'–'13' value space. Förderschule/Inklusion is
	// absent from the AI school-type list; it stays on the inclusionAdapted flag.
	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (useAiFilters) fetchAiFilterValuesAction();
	}, [useAiFilters]);

	useEffect(() => {
		if (!useAiFilters) return;
		const toOptions = (items: { name: string; searchId: string }[], selected?: string[]) =>
			items.map(({ name, searchId }) => ({
				name,
				value: searchId,
				isSelected: selected?.includes(searchId) ?? false,
			}));
		setFilterOptions((prev) => ({
			...prev,
			// SEAR-460: preserve the SEAR-282 "selected options first" ordering that
			// Effect A applies to subjects. That effect only re-runs on
			// [isOpen, requestFilters], so once the AI fetch resolves it would leave
			// the subjects purely alphabetical. sort() is stable, so chaining keeps
			// alphabetical order within the selected/unselected groups.
			subject: sortAlphabetically(toOptions(aiFilterValues.subjects, requestFilters.subject), {
				field: 'name',
			}).sort((a, b) => Number(b.isSelected) - Number(a.isSelected)),
			schoolType: toOptions(aiFilterValues.schoolTypes, requestFilters.schoolType),
		}));
	}, [useAiFilters, aiFilterValues, requestFilters.subject, requestFilters.schoolType]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		const publisherOptions = publishers.map((publisher) => ({
			name: publisher.textValue,
			value: publisher.id,
			isSelected: requestFilters.publisher?.includes(publisher.id),
		}));
		if (publisherOptions.length > 0) {
			setFilterOptions((prev) => ({ ...prev, publisher: publisherOptions }));
		}
	}, [publishers.length]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		const workloadOptions = workloads.map((workload) => ({
			name: workload.name,
			value: workload.id,
			isSelected: requestFilters.workload?.includes(workload.id),
		}));
		if (workloadOptions.length > 0) {
			setFilterOptions((prev) => ({ ...prev, workload: workloadOptions }));
		}
	}, [workloads.length]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		const selectedSubjects = filterOptions.subject.filter((subject) => subject.isSelected);
		const subTopicOptions: FilterOption[] = [];

		selectedSubjects.reduce((acc, subject) => {
			const subTopics = subtopicsSelector[subject.value];
			if (subTopics) {
				const subjectSubTopics = subTopics.map((subTopic) => ({
					name: subTopic.name,
					value: subTopic.value,
					isSelected: requestFilters.subTopic?.includes(subTopic.value),
				}));
				if (subjectSubTopics.length > 0) acc.push(...subjectSubTopics);
			}
			return acc;
		}, subTopicOptions);

		setFilterOptions((prev) => ({ ...prev, subTopic: subTopicOptions }));
	}, [Object.keys(subtopicsSelector).length, numberOfSelectedSubjects]);

	const selectOption = (accordion: AccordionType, value: FilterOption['value']) => {
		setFilterOptions((prev) => ({
			...prev, // Keep the other accordions as they are
			[accordion]: prev[accordion].map((filterOption) => {
				if (filterOption.value === value) {
					// If this is the selected option
					return { ...filterOption, isSelected: !filterOption.isSelected }; // Toggle isSelected
				}
				return filterOption; // Otherwise, return the option as it is
			}),
		}));

		if (accordion === 'subject') {
			searchTopicsAction(value);
		}
	};

	const getTruncationLines = () => {
		if (isMobile) return 5;
		if (isTablet) return 3;
		return 2;
	};

	const getSelectedValues = (accordion: AccordionType) => {
		return filterOptions[accordion]
			.filter((option) => option.isSelected)
			.map((option) => option.value);
	};

	const saveFilters = () => {
		const arrayFilters: Partial<SearchFilters> = {};
		const booleanFilterKeys: BooleanAccordionType[] = ['inclusionAdapted'];

		Object.keys(filterOptions).forEach((key) => {
			const accordion = key as AccordionType;
			const selected = getSelectedValues(accordion);

			if (booleanFilterKeys.includes(accordion as BooleanAccordionType)) {
				arrayFilters[accordion as BooleanAccordionType] = selected.length > 0;
			} else {
				const arrayKey = accordion as ArrayAccordionType;
				(arrayFilters as Record<ArrayAccordionType, string[]>)[arrayKey] = selected;
			}
		});

		updateQueryParams(arrayFilters);
	};

	const handleSave = () => {
		saveFilters();
		if (handleClose) handleClose();
	};

	const clearFilters = () => {
		// Set flag to prevent useEffect from overwriting our cleared state
		isClearingRef.current = true;

		// Clear sessionStorage FIRST so requestFilters will use empty filters if URL is empty
		const emptyFilters: SearchFilters = {
			sortBy: searchSettings.defaultSortBy,
		};
		if (type === 'core') {
			searchResultsStorageManager.setSearchFilters(emptyFilters);
		} else {
			searchResultsStorageManager.setCourseSearchFilters(emptyFilters);
		}

		// Clear local state
		setFilterOptions((prev) => {
			const newFilterOptions = { ...prev };
			Object.keys(newFilterOptions).forEach((key) => {
				newFilterOptions[key as AccordionType] = newFilterOptions[key as AccordionType].map(
					(option) => ({
						...option,
						isSelected: false,
					})
				);
			});

			return newFilterOptions;
		});

		// Update URL to reflect cleared filters (removes all filter params)
		// This is async, but sessionStorage is already cleared so requestFilters will be correct
		updateQueryParams(emptyFilters);
	};

	const divider = `1px solid ${colors.neutral200}`;

	const accordionsConfig: Array<{
		title: string;
		key: AccordionType;
		props?: Partial<SearchFiltersAccordionProps>;
	}> = [
		{
			title: t(filterLabels.subject),
			key: 'subject',
			props: {
				truncateOptions: { numberOfLines: getTruncationLines() },
			},
		},
		{
			title: t(filterLabels.classYear),
			key: 'classYear',
		},
		{
			title: t(filterLabels.schoolType),
			key: 'schoolType',
		},
		{
			title: t(filterLabels.publisher),
			key: 'publisher',
			props: {
				truncateOptions: { numberOfLines: getTruncationLines() },
			},
		},
		{
			title: t(filterLabels.subTopic),
			key: 'subTopic',
			props: {
				alert: {
					message: t('Select a subject to see subtopics'),
				},
				isDisabled: !isAnySubjectSelected,
			},
		},
		{
			title: t(filterLabels.learningFormat),
			key: 'learningFormat',
		},
		{
			title: t(filterLabels.teachingFormat),
			key: 'teachingFormat',
			props: { isNew: true },
		},
		{
			title: t(filterLabels.teachingMethod),
			key: 'teachingMethod',
			props: { isNew: true },
		},
		{
			title: t(filterLabels.pageContentType),
			key: 'pageContentType',
			props: { isNew: true },
		},
		{
			title: t(filterLabels.inclusionAdapted),
			key: 'inclusionAdapted',
			props: { isNew: true },
		},
		{
			title: t(filterLabels.workload),
			key: 'workload',
		},
		{
			title: t(filterLabels.courseTheme),
			key: 'courseTheme',
		},
		{
			title: t(filterLabels.courseType),
			key: 'courseType',
		},
		{
			title: t(filterLabels.courseDuration),
			key: 'courseDuration',
		},
	];

	return (
		<CustomDialog
			dataTestId="search-filters-dialog"
			open={isOpen}
			onClose={handleClose ?? (() => {})}
			title={t('Filters')}
			backButton={{
				label: t('Clear all'),
				onClick: clearFilters,
				variant: 'outlined',
				color: 'primary',
			}}
			primaryButton={{
				label: t('Show results'),
				onClick: handleSave,
			}}
			dialogProps={{
				fullWidth: true,
				maxWidth: isXsScreen ? 'xl' : 'md',
				fullScreen: isXsScreen,
			}}
			dialogContentSx={{ borderBottom: divider, borderTop: divider }}
		>
			{filtersOrder.map((key) => {
				const accordion = accordionsConfig.find(({ key: accordionKey }) => accordionKey === key);
				if (!accordion) return null;
				const { title, props } = accordion;
				return (
					<SearchFiltersAccordion
						key={key}
						ref={refs[key]}
						title={title}
						isExpanded={expanded[key]}
						options={filterOptions[key]}
						onLoad={() => {
							if (scrollTo === key) {
								setTimeout(
									() => (
										refs[key]?.current?.scrollIntoView({
											behavior: 'smooth',
											block: 'start',
										}),
										SCROLL_DELAY
									)
								);
							}
						}}
						selectOption={(value) => selectOption(key, value)}
						toggleAccordion={() => {
							toggleAccordion(key);
							!expanded[key] &&
								setTimeout(
									() =>
										refs[key]?.current?.scrollIntoView({
											behavior: 'smooth',
											block: 'start',
										}),
									SCROLL_DELAY
								);
						}}
						{...props}
					/>
				);
			})}
		</CustomDialog>
	);
};

export default SearchFiltersDialog;
